import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Upload, 
  Search,
  FileText,
  BookOpen,
  Scale,
  FolderOpen,
  Download,
  Loader2,
  File,
  FileSpreadsheet,
  Sparkles,
  ExternalLink,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category metadata
const CATEGORY_META = {
  STANDARD_FORMS: {
    label: 'Standard Forms',
    icon: Scale,
    description: 'Federal acquisition forms (SF33, SF1449)',
    color: 'text-blue-600 bg-blue-50'
  },
  MANAGEMENT_PLANS: {
    label: 'Management Plans',
    icon: BookOpen,
    description: 'QCP, Staffing, PMP templates',
    color: 'text-emerald-600 bg-emerald-50'
  },
  TECHNICAL_ATTACHMENTS: {
    label: 'Technical Attachments',
    icon: FileText,
    description: 'Compliance matrix, cross-reference tables',
    color: 'text-purple-600 bg-purple-50'
  }
};

// File type icons
const FILE_ICONS = {
  PDF: { icon: FileText, color: 'text-red-500' },
  DOCX: { icon: File, color: 'text-blue-500' },
  XLSX: { icon: FileSpreadsheet, color: 'text-emerald-500' },
  default: { icon: FileText, color: 'text-slate-500' }
};

export default function GlobalAssetLibrary() {
  const { getAuthHeaders, user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const fetchTemplates = useCallback(async () => {
    setIsLoading(true);
    try {
      let url = `${API_URL}/api/admin/templates`;
      if (searchQuery) {
        url += `?search=${encodeURIComponent(searchQuery)}`;
      }
      
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.items);
        setCategories(data.categories);
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      toast.error('Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, getAuthHeaders]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleDownload = async (template) => {
    try {
      // Track download
      await fetch(`${API_URL}/api/admin/templates/${template.id}/download`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      // If there's a file URL, open it
      if (template.file_url) {
        window.open(template.file_url, '_blank');
      } else {
        toast.info('Template file not yet uploaded');
      }
      
      // Refresh to update download count
      fetchTemplates();
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  // Filter templates by category
  const filteredTemplates = activeCategory === 'all' 
    ? templates 
    : templates.filter(t => t.category === activeCategory);

  const totalCount = Object.values(categories).reduce((a, b) => a + b, 0);

  return (
    <div data-testid="global-asset-library">
      <PageHeader 
        title="Global Asset Library"
        description="Master templates and references for federal contracting"
        action={
          user?.role === 'SUPER_ADMIN' && (
            <Button className="bg-teal-700 hover:bg-teal-800 text-white">
              <Upload className="w-4 h-4 mr-2" />
              Upload Template
            </Button>
          )
        }
      />
      
      <div className="p-8">
        {/* AI Toolbox Header */}
        <Card className="mb-6 border-0 bg-gradient-to-r from-slate-900 to-slate-800 text-white overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-teal-500/20 rounded-xl">
                <Sparkles className="w-8 h-8 text-teal-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  AI Agent Toolbox
                </h2>
                <p className="text-slate-300 text-sm">
                  These templates power the AI "Shredder" agent's recommendations. 
                  When it identifies a QCP requirement, it suggests the matching template from here.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Toolbar */}
        <div className="flex items-center justify-between mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              placeholder="Search templates..."
              className="pl-10 w-64 border-slate-200"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="template-search"
            />
          </div>
          
          <Button 
            variant="outline"
            size="sm"
            onClick={() => fetchTemplates()}
            disabled={isLoading}
          >
            <RefreshCw className={cn("w-4 h-4 mr-2", isLoading && "animate-spin")} />
            Refresh
          </Button>
        </div>

        {/* Category Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {Object.entries(CATEGORY_META).map(([key, meta]) => {
            const count = categories[key] || 0;
            const IconComponent = meta.icon;
            
            return (
              <Card 
                key={key}
                className={cn(
                  "border-slate-200 shadow-sm cursor-pointer transition-all hover:shadow-md hover:border-slate-300",
                  activeCategory === key && "ring-2 ring-teal-500 border-teal-500"
                )}
                onClick={() => setActiveCategory(activeCategory === key ? 'all' : key)}
                data-testid={`category-${key}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className={cn("p-2.5 rounded-lg", meta.color)}>
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {count} items
                    </Badge>
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {meta.label}
                  </h3>
                  <p className="text-xs text-slate-500">
                    {meta.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Templates List */}
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <FolderOpen className="w-12 h-12 text-slate-300 mb-3" />
                <p className="text-slate-500">No templates found</p>
              </div>
            ) : (
              <div className="space-y-2 p-3">
                {filteredTemplates.map((template) => {
                  const fileInfo = FILE_ICONS[template.file_type] || FILE_ICONS.default;
                  const FileIcon = fileInfo.icon;
                  const categoryMeta = CATEGORY_META[template.category];
                  
                  return (
                    <div 
                      key={template.id}
                      className="p-4 rounded-lg border border-slate-100 hover:bg-slate-50 hover:border-slate-200 transition-colors flex items-center justify-between"
                      data-testid={`template-${template.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={cn("p-2 rounded-lg", categoryMeta?.color || 'bg-slate-100')}>
                          <FileIcon className={cn("w-5 h-5", fileInfo.color)} />
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {template.name}
                          </h4>
                          <p className="text-sm text-slate-500 mt-0.5">
                            {template.description}
                          </p>
                          <div className="flex items-center gap-3 mt-2">
                            <Badge variant="outline" className="text-xs">
                              {categoryMeta?.label || template.category}
                            </Badge>
                            <span className="text-xs text-slate-400">
                              v{template.version}
                            </span>
                            <span className="text-xs text-slate-400">
                              {template.download_count} downloads
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(template)}
                        className="gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stats Footer */}
        <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <div className="flex items-center justify-between text-sm text-slate-500">
            <span>
              Showing {filteredTemplates.length} of {totalCount} templates
            </span>
            <span className="flex items-center gap-1">
              <Sparkles className="w-4 h-4 text-teal-500" />
              Templates are AI-searchable by the Shredder agent
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
